import { create, toJson } from "@bufbuild/protobuf";
import { CreateLinkRequestSchema, DeleteLinkRequestSchema, GetDomainPublicConfigResponseSchema, GetLinkRequestSchema, UpdateLinkRequestSchema, type CreateLinkRequest, type CreateLinkResponse, type DeleteLinkRequest, type GetDomainPublicConfigResponse, type GetLinkRequest, type GetLinkResponse, type UpdateLinkRequest, type UpdateLinkResponse } from "#gen/links/v1/links_pb.js";

// Interface with the Links service via a middleware

const baseRoute = "/api/links";
const baseOptions = {
	method: "POST",
	headers: {
		"Content-Type": "application/json",
	},
};

export async function getLink(req: GetLinkRequest): Promise<GetLinkResponse> {
	const res = await fetch(`${baseRoute}/getLink`, {
		...baseOptions,
		body: JSON.stringify(toJson(GetLinkRequestSchema, req)),
	});
	if (!res.ok) {
		throw new Error(`Unable to get link (${res.statusText})`);
	}
	return (await res.json()) as GetLinkResponse;
}

export async function createLink(req: CreateLinkRequest): Promise<CreateLinkResponse> {
	const res = await fetch(`${baseRoute}/createLink`, {
		...baseOptions,
		body: JSON.stringify(toJson(CreateLinkRequestSchema, req)),
	});
	const value = await res.json();
	if (!res.ok) {
		throw new Error(`Unable to create link (${value.message})`);
	}
	return value as CreateLinkResponse;
}

export async function updateLink(req: UpdateLinkRequest): Promise<UpdateLinkResponse> {
	const res = await fetch(`${baseRoute}/updateLink`, {
		...baseOptions,
		body: JSON.stringify(toJson(UpdateLinkRequestSchema, req)),
	});
	const value = await res.json();
	if (!res.ok) {
		throw new Error(`Unable to update link (${value.message})`);
	}
	return value as UpdateLinkResponse;
}

export async function deleteLink(req: DeleteLinkRequest): Promise<void> {
	const res = await fetch(`${baseRoute}/deleteLink`, {
		...baseOptions,
		body: JSON.stringify(toJson(DeleteLinkRequestSchema, req)),
	});
	if (!res.ok) {
		throw new Error(`Error deleting link: ${res.statusText}`);
	}
}

export async function getDpaDomainInfo(fetchFn: (input: RequestInfo, init?: RequestInit) => Promise<Response>, domainId: string): Promise<GetDomainPublicConfigResponse | undefined> {
	const res = await fetchFn("/links.v1.LinksService/GetDomainPublicConfig", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			domainId,
		}),
	});
	const response = await res.json();
	return create(GetDomainPublicConfigResponseSchema, {
		oneTrustId: response?.oneTrustId ?? "",
		domain: response?.domain ?? "",
		privacyPolicyLinkHash: response?.privacyPolicyLinkHash ?? "",
		cookiePreferencesLinkLabel: response?.cookiePreferencesLinkLabel ?? "",
		doNotSellPolicyLinkUrl: response?.doNotSellPolicyLinkUrl ?? "",
		doNotSellPolicyLinkLabel: response?.doNotSellPolicyLinkLabel ?? "",
		privacyPreferencesUrl: response?.privacyPreferencesUrl ?? "",
		privacyPreferencesLabel: response?.privacyPreferencesLabel ?? "",
		privacySmileUrl: response?.privacySmileUrl ?? "",
		privacyPolicyLinkLabel: response?.privacyPolicyLinkLabel ?? "",
		isGlobalDefault: response?.isGlobalDefault ?? false,
		privacyLinks: (response?.privacyLinks ?? []).map((link: { predefinedKey?: string; custom?: { id?: string; label?: string; url?: string } }) => {
			if (link?.custom) {
				return { link: { case: "custom" as const, value: { id: link.custom.id ?? "", label: link.custom.label ?? "", url: link.custom.url ?? "" } } };
			}
			return { link: { case: "predefinedKey" as const, value: link?.predefinedKey ?? "" } };
		}),
	});
}
